import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { format, startOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay, parseISO, startOfMonth, endOfMonth, addMonths, subMonths, addDays, subDays, getDay } from 'date-fns';

interface LoadingStateProps {
  isLoading: boolean;
  error?: string | null;
  onRetry?: () => void;
  loadingText?: string;
  className?: string;
  variant?: 'spinner' | 'skeleton' | 'dots' | 'pulse';
  size?: 'sm' | 'md' | 'lg';
  showDelay?: number; // Delay before showing loading state
}

export const LoadingState = ({
  isLoading,
  error,
  onRetry,
  loadingText = 'Loading...',
  className,
  variant = 'spinner',
  size = 'md',
  showDelay = 200
}: LoadingStateProps) => {
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => setShowLoading(true), showDelay);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(false);
    }
  }, [isLoading, showDelay]);

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };

  const LoadingSpinner = () => (
    <div className={cn('flex items-center justify-center gap-2', className)}>
      <Loader2 className={cn('animate-spin', sizeClasses[size])} />
      {loadingText && (
        <span className={cn(
          'text-muted-foreground',
          size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base'
        )}>
          {loadingText}
        </span>
      )}
    </div>
  );

  const LoadingDots = () => (
    <div className={cn('flex items-center justify-center gap-1', className)}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className={cn('bg-primary rounded-full',
            size === 'sm' ? 'h-1 w-1' : size === 'lg' ? 'h-3 w-3' : 'h-2 w-2'
          )}
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{
            duration: 1,
            repeat: Infinity,
            delay: i * 0.2,
            ease: 'easeInOut'
          }}
        />
      ))}
    </div>
  );

  const LoadingPulse = () => (
    <div className={cn('flex items-center justify-center', className)}>
      <motion.div
        className={cn('bg-primary/20 rounded-full', sizeClasses[size])}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
    </div>
  );

  const LoadingSkeleton = () => (
    <div className={cn('space-y-3', className)}>
      <div className="animate-pulse flex space-x-4">
        <div className="rounded-full bg-muted h-10 w-10"></div>
        <div className="flex-1 space-y-2 py-1">
          <div className="h-4 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
        </div>
      </div>
      <div className="animate-pulse space-y-2">
        <div className="h-4 bg-muted rounded"></div>
        <div className="h-4 bg-muted rounded w-5/6"></div>
      </div>
    </div>
  );

  const renderLoading = () => {
    switch (variant) {
      case 'dots':
        return <LoadingDots />;
      case 'pulse':
        return <LoadingPulse />;
      case 'skeleton':
        return <LoadingSkeleton />;
      default:
        return <LoadingSpinner />;
    }
  };

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn('flex flex-col items-center justify-center p-4', className)}
      >
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-destructive text-center mb-4">{error}</p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline">
            Refresh
          </Button>
        )}
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      {(isLoading && showLoading) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {renderLoading()}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Network status indicator
export const NetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-0 left-0 right-0 z-50 bg-destructive text-destructive-foreground p-2 text-center text-sm"
    >
      <div className="flex items-center justify-center gap-2">
        <WifiOff className="h-4 w-4" />
        You're currently offline. Some features may not work.
      </div>
    </motion.div>
  );
};

// Enhanced loading wrapper for async operations
interface AsyncWrapperProps {
  children: React.ReactNode;
  isLoading: boolean;
  error?: string | null;
  onRetry?: () => void;
  loadingComponent?: React.ReactNode;
  minLoadingTime?: number; // Minimum time to show loading state
}

export const AsyncWrapper = ({
  children,
  isLoading,
  error,
  onRetry,
  loadingComponent,
  minLoadingTime = 500
}: AsyncWrapperProps) => {
  const [shouldShowLoading, setShouldShowLoading] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setShouldShowLoading(true);
      setMinTimeElapsed(false);

      const timer = setTimeout(() => {
        setMinTimeElapsed(true);
      }, minLoadingTime);

      return () => clearTimeout(timer);
    } else if (minTimeElapsed) {
      setShouldShowLoading(false);
    }
  }, [isLoading, minLoadingTime, minTimeElapsed]);

  useEffect(() => {
    if (!isLoading && minTimeElapsed) {
      setShouldShowLoading(false);
    }
  }, [isLoading, minTimeElapsed]);

  if (shouldShowLoading) {
    return loadingComponent || <LoadingState isLoading={true} />;
  }

  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto card-no-animation">
        <CardHeader className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <CardTitle>Something went wrong</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardContent>
          {onRetry && (
            <Button onClick={onRetry} className="w-full">
              Refresh
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
};

export default LoadingState;